import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextProviderInvoice } from './ProviderInvoice';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderInvoiceDA } from '../../../../services/admin/provider.invoice.service';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Error } from '../../../../models/error.model';
import { CompanyProviderInvoices } from '../../../../models/company.provider.invoices.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { ProviderInvoiceGroup } from '../../../../models/provider.invoice.group.model';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { colorStatusPayment, colorStatusProviderInvoice, colorValidated } from '../../../../scripts/badge.color.script';
import { accumulatedAmount, isVatSubject } from '../../../../scripts/financial.calculation.script';
import { nameGroup } from '../../../../libraries/group.name.library';
import { getError } from '../../../../languages/translations/response';
import { validityBoolean } from '../../../../libraries/validation.library';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { sortColumn } from '../../../../scripts/table.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';
import { fetchDocumentPdf } from '../../../../scripts/document.pdf.script';
import { usePdfViewer } from '../../../../contexts/PdfViewerContext';
import { sanitizeFilename } from '../../../../scripts/document.filename.script';

import AppPurchaseListHeader from '../component/PurchaseListHeader';
import AppPagination from '../../../../components/element/Pagination';
import AppSortTable from '../../../../components/element/SortTable';
import AppPreloader from '../../../../components/Preloader';
import AppNone from '../../../../components/element/None';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';

export interface ProviderInvoiceListListPageProps {};

let errorResponse: Error, providerInvoicesResponse: CompanyProviderInvoices;

const ProviderInvoiceListPage: React.FunctionComponent<ProviderInvoiceListListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany} = UseOutletContextProviderInvoice()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState({filter: false, group: false})
  const [filterState, setFilterState] = useState({interval: '', start_date: '', end_date: '', contact: '', contact_label: '', treasury: '', validity: '', status: '', payment: '', sort_field: '', sort_mode: '', group: '', search: ''})
  const filterRef = useRef(filterState)
  const filter = filterRef.current
  const setFilter = (v: React.SetStateAction<typeof filterState>) => { const n = typeof v === 'function' ? v(filterRef.current) : v; filterRef.current = n; setFilterState(n) }
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [providerInvoices, setProviderInvoices] = useState<ProviderInvoice[] | null>(null)
  const [providerInvoiceGroups, setProviderInvoiceGroups] = useState<ProviderInvoiceGroup[] | null>(null)
  const [storage, setStorage] = useState(null)
  const { openUrl } = usePdfViewer();
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const vatSubject = isVatSubject(settingsForCompany, companyForUser);
  // Nombre de colonnes AVANT le bloc montants (payé/dette/TVA/total). La colonne TVA fait partie du bloc montants.
  const COLS_BEFORE_AMOUNTS = 6;

  const executeViewPdf = async (id: string, filename: string) => {
    if (!companyForUser) return;
    setPdfLoading(id);
    try {
      const url = await fetchDocumentPdf(companyForUser.id, 'provider-invoices', id);
      openUrl(url, `${sanitizeFilename(filename)}.pdf`);
    } catch {
      // fetchDocumentPdf throws with a readable message on error
    } finally {
      setPdfLoading(null);
    }
  };

  const loadProviderInvoices = async (id_company: string, page: number = 1) => {
    const f = filterRef.current
    await ProviderInvoiceDA.getProviderInvoices(id_company, f.interval, f.start_date, f.end_date, f.contact, f.treasury, f.validity, f.status, f.payment, f.sort_field, f.sort_mode, f.group, page, pagination.limit, f.search).then( (response) => {
      if (response.status === 200) {
        providerInvoicesResponse = response.data

        const filterData = {
          company_id: id_company,
          interval: f.interval,
          start_date: f.start_date,
          end_date: f.end_date,
          contact: f.contact,
          contact_label: f.contact_label,
          treasury: f.treasury,
          validity: f.validity,
          status: f.status,
          payment: f.payment,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          group: f.group,
          search: f.search
        }
        localStorage.setItem('filter_provider_invoices', JSON.stringify(filterData))

        const groupBy = filterData.group
        const items = (providerInvoicesResponse.items ?? providerInvoicesResponse.provider_invoices ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status), payment_status: item.payment_status ? normalizeStatus(item.payment_status) : item.payment_status }))
        const groups = (providerInvoicesResponse.groups ?? []).map((group: any) => ({ ...group, items: (group.items ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status), payment_status: item.payment_status ? normalizeStatus(item.payment_status) : item.payment_status })) }))

        localStorage.setItem('nav_provider_invoices', JSON.stringify({ company_id: id_company, ids: items.map((item: any) => item.id) }))

        if (!groupBy || groupBy.length === 0) {
          setProviderInvoiceGroups([])
          setProviderInvoices(items)

          const totalPages = providerInvoicesResponse.pagination?.pages || 1
          if (page > totalPages && totalPages >= 1) {
            setUrlPage(totalPages)
            loadProviderInvoices(id_company, totalPages)
            return
          }
          let pagesOfPagination: number[] = []
          for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
          setPagination({...pagination, current: providerInvoicesResponse.pagination?.page || 1, total: providerInvoicesResponse.pagination?.total || 0, pages: pagesOfPagination})
          setUrlPage(providerInvoicesResponse.pagination?.page || 1)
        } else {
          setProviderInvoices([])
          setProviderInvoiceGroups(groups)
          setPagination({current: 1, limit: 20, total: 0, pages: [1]})
          setUrlPage(1)
        }

        setButton({filter: Boolean(filterData.interval || filterData.start_date || filterData.end_date || filterData.contact || filterData.treasury || filterData.validity || filterData.status || filterData.payment), group: Boolean(groupBy)})
        setStorage((localStorage.getItem('filter_provider_invoices')) ? JSON.parse(localStorage.getItem('filter_provider_invoices')!) : null)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadProviderInvoices(companyForUser.id, current)
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadProviderInvoices(companyForUser.id, 1)
    }
  }

  useEffect( () => {
    let filter_provider_invoices: any = (localStorage.getItem('filter_provider_invoices')) ? JSON.parse(localStorage.getItem('filter_provider_invoices')!) : null

    if (companyForUser) {
      const isMatch = filter_provider_invoices && filter_provider_invoices.company_id === companyForUser.id
      setFilter({
        interval: isMatch ? filter_provider_invoices.interval : '',
        start_date: isMatch ? filter_provider_invoices.start_date : '',
        end_date: isMatch ? filter_provider_invoices.end_date : '',
        contact: isMatch ? filter_provider_invoices.contact : '',
        contact_label: isMatch ? (filter_provider_invoices.contact_label ?? '') : '',
        treasury: isMatch ? filter_provider_invoices.treasury : '',
        validity: isMatch ? filter_provider_invoices.validity : '',
        status: isMatch ? filter_provider_invoices.status : '',
        payment: isMatch ? filter_provider_invoices.payment : '',
        sort_field: isMatch ? filter_provider_invoices.sort_field : '',
        sort_mode: isMatch ? filter_provider_invoices.sort_mode : '',
        group: isMatch ? filter_provider_invoices.group : '',
        search: isMatch ? (filter_provider_invoices.search ?? '') : ''
      })

      setProviderInvoices(null)
      setProviderInvoiceGroups(null)
      setButton({filter: false, group: false})
      loadProviderInvoices(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.providerInvoices, branch: lang.labels.listProviderInvoices}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && providerInvoices && providerInvoiceGroups
        ?
        <div className="card card-flush">
          <AppPurchaseListHeader purchase={"invoice"} storage={storage} button={button} filter={filter} records={providerInvoices} recordGroups={providerInvoiceGroups} setFilter={setFilter} loadDocuments={loadProviderInvoices} totalItems={!button.group ? pagination.total : undefined}></AppPurchaseListHeader>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_date")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"created_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("provider_invoice_number")}>
                          <span className="me-1">{lang.labels.invoiceNumber}</span>
                          <AppSortTable attribute={"provider_invoice_number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("concerning")}>
                          <span className="me-1">{lang.labels.concerning}</span>
                          <AppSortTable attribute={"concerning"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("provider")}>
                          <span className="me-1">{lang.labels.provider}</span>
                          <AppSortTable attribute={"provider"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("validity")}>
                          <span className="me-1">{lang.labels.validity}</span>
                          <AppSortTable attribute={"validity"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.paid")}>
                          <span className="me-1">{lang.labels.amountPaid}</span>
                          <AppSortTable attribute={"amount.paid"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.debt")}>
                          <span className="me-1">{lang.labels.amountDebt}</span>
                          <AppSortTable attribute={"amount.debt"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      {vatSubject && <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.tax_total")}>
                          <span className="me-1">{lang.labels.vatAmount}</span>
                          <AppSortTable attribute={"amount.tax_total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>}
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.total")}>
                          <span className="me-1">{lang.labels.amountTotal}</span>
                          <AppSortTable attribute={"amount.total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-50px text-center"></th>
                      <th className="w-50px text-center">
                        <span>{lang.labels.vouchers}</span>
                      </th>
                    </tr>
                  </thead>
                  { providerInvoices.length > 0 || providerInvoiceGroups.length > 0
                    ?
                    <>
                      { button.group
                        ?
                          <>
                            { providerInvoiceGroups.map (( (item, index) => { return (
                              <tbody key={index} className="text-secondary fw-bold">
                                <tr>
                                  <td colSpan={11 + (vatSubject ? 1 : 0)} className="text-uppercase fw-bolder fs-7">
                                    <div className="d-flex align-items-center">
                                      <span className="svg-icon svg-icon-3 svg-icon-gray-900 me-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path d="M22 12C22 12.9 21.9 13.8 21.7 14.6L5 4.89999C6.8 3.09999 9.3 2 12 2C17.5 2 22 6.5 22 12Z" fill="black"/>
                                          <path opacity="0.3" d="M3.7 17.5C2.6 15.9 2 14 2 12C2 9.20003 3.1 6.70002 5 4.90002L9.3 7.40002V14.2L3.7 17.5ZM17.2 12L5 19.1C6.8 20.9 9.3 22 12 22C16.6 22 20.5 18.8 21.7 14.6L17.2 12Z" fill="black"/>
                                        </svg>
                                      </span>
                                      <span className="me-3">{lang.labels.group} {index + 1}:</span>
                                      {nameGroup(lang, filter.group, item.group_detail)}
                                    </div>
                                  </td>
                                </tr>
                                { item.provider_invoices.map (( (sub_item, sub_index) => { return (
                                  <tr key={sub_index} className="row-dashed">
                                    <td className="text-nowrap">
                                      <AppNone condition={true} display={dateFormat(lang.code, sub_item.created_date)}></AppNone>
                                    </td>
                                    <td>
                                      { sub_item.status === "draft" && !(sub_item as any).id_source_company
                                        ?
                                        <Link to={"/main/purchase/invoice/update/" + sub_item.id}className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.provider_invoice_number}</Link>
                                        :
                                        <Link to={"/main/purchase/invoice/view/" + sub_item.id}className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.provider_invoice_number}</Link>
                                      }
                                    </td>
                                    <td>
                                      <AppNone condition={true} display={sub_item.concerning ?? ''}></AppNone>
                                    </td>
                                    <td>
                                      <AppNone condition={true} display={formatContact(sub_item.provider)}></AppNone>
                                    </td>
                                    <td>
                                      <span className={`badge badge-light-${colorValidated(sub_item.validated)} fs-7`}>{validityBoolean(lang, sub_item.validated)}</span>
                                    </td>
                                    <td>
                                      <span className={`badge badge-light-${colorStatusProviderInvoice(sub_item.status)} fs-7`}>
                                        {statusDocumentValue(lang, sub_item.status)}
                                      </span>
                                    </td>
                                    <td className="text-end">
                                      <span className={`badge badge-light-${colorStatusPayment(sub_item.payment_status)} fs-7`}>
                                        <AppNone condition={true} display={`${sub_item.currency?.code ?? ''} ${formatMoney(sub_item.amount?.paid ?? 0)}`}></AppNone>
                                      </span>
                                    </td>
                                    <td className="text-end">
                                      <span className={`badge badge-light-${colorStatusPayment(sub_item.payment_status)} fs-7`}>
                                        <AppNone condition={true} display={`${sub_item.currency?.code ?? ''} ${formatMoney(sub_item.amount?.debt ?? 0)}`}></AppNone>
                                      </span>
                                    </td>
                                    {vatSubject && <td className="text-end text-nowrap">{sub_item.currency?.code ?? ''} {formatMoney(sub_item.amount?.tax_total ?? 0)}</td>}
                                    <td className="text-end text-nowrap">
                                      <AppNone condition={true} display={`${sub_item.currency?.code ?? ''} ${formatMoney(sub_item.amount?.total ?? 0)}`}></AppNone>
                                      {sub_item.foreign_currency?.code &&<div className="fs-9 fw-normal text-muted">{sub_item.foreign_currency.code} {formatMoney(sub_item.foreign_currency.amount?.total ?? 0)}</div>}
                                    </td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light-primary"
                                        title={lang.labels.view}
                                        onClick={() => executeViewPdf(sub_item.id, sub_item.provider_invoice_number)}
                                        disabled={pdfLoading === sub_item.id}
                                      >
                                        {pdfLoading === sub_item.id
                                          ? <span className="spinner-border spinner-border-sm"></span>
                                          : <i className="bi bi-eye fs-5"></i>
                                        }
                                      </button>
                                    </td>
                                    <td className="text-center">
                                      <ButtonVouchers vouchers={sub_item.vouchers} />
                                    </td>
                                  </tr>
                                )}))}
                                <tr className={`fw-bolder fs-7 ${index < providerInvoiceGroups.length - 1 && "row-dashed"}`}>
                                  <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                                  <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.provider_invoices).paid)}</td>
                                  <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.provider_invoices).debt)}</td>
                                  {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.provider_invoices).tax)}</td>}
                                  <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.provider_invoices).total)}</td>
                                  <td colSpan={2}></td>
                                </tr>
                              </tbody>
                            )}))}
                          </>
                        :
                        <>
                          <tbody className="text-secondary fw-bold">
                            { providerInvoices.map (( (item, index) => { return (
                              <tr key={index} className="row-dashed">
                                <td className="text-nowrap">
                                  <AppNone condition={true} display={dateFormat(lang.code, item.created_date)}></AppNone>
                                </td>
                                <td>
                                  { item.status === "draft" && !(item as any).id_source_company
                                    ?
                                    <Link to={"/main/purchase/invoice/update/" + item.id}className="fw-bolder text-nowrap text-dark text-hover-primary">{item.provider_invoice_number}</Link>
                                    :
                                    <Link to={"/main/purchase/invoice/view/" + item.id}className="fw-bolder text-nowrap text-dark text-hover-primary">{item.provider_invoice_number}</Link>
                                  }
                                </td>
                                <td>
                                  <AppNone condition={true} display={item.concerning ?? ''}></AppNone>
                                </td>
                                <td>
                                  <AppNone condition={true} display={formatContact(item.provider)}></AppNone>
                                </td>
                                <td>
                                  <span className={`badge badge-light-${colorValidated(item.validated)} fs-7`}>{validityBoolean(lang, item.validated)}</span>
                                </td>
                                <td>
                                  <span className={`badge badge-light-${colorStatusProviderInvoice(item.status)} fs-7`}>
                                    {statusDocumentValue(lang, item.status)}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <span className={`badge badge-light-${colorStatusPayment(item.payment_status)} fs-7`}>
                                    <AppNone condition={true} display={`${item.currency?.code ?? ''} ${formatMoney(item.amount?.paid ?? 0)}`}></AppNone>
                                  </span>
                                </td>
                                <td className="text-end">
                                  <span className={`badge badge-light-${colorStatusPayment(item.payment_status)} fs-7`}>
                                    <AppNone condition={true} display={`${item.currency?.code ?? ''} ${formatMoney(item.amount?.debt ?? 0)}`}></AppNone>
                                  </span>
                                </td>
                                {vatSubject && <td className="text-end text-nowrap">{item.currency?.code ?? ''} {formatMoney(item.amount?.tax_total ?? 0)}</td>}
                                <td className="text-end text-nowrap">
                                  <AppNone condition={true} display={`${item.currency?.code ?? ''} ${formatMoney(item.amount?.total ?? 0)}`}></AppNone>
                                  {item.foreign_currency?.code &&<div className="fs-9 fw-normal text-muted">{item.foreign_currency.code} {formatMoney(item.foreign_currency.amount?.total ?? 0)}</div>}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-primary"
                                    title={lang.labels.view}
                                    onClick={() => executeViewPdf(item.id, item.provider_invoice_number)}
                                    disabled={pdfLoading === item.id}
                                  >
                                    {pdfLoading === item.id
                                      ? <span className="spinner-border spinner-border-sm"></span>
                                      : <i className="bi bi-eye fs-5"></i>
                                    }
                                  </button>
                                </td>
                                <td className="text-center">
                                  <ButtonVouchers vouchers={item.vouchers} />
                                </td>
                              </tr>
                            )}))}
                          </tbody>
                          <tfoot>
                            <tr className="fw-bolder fs-7">
                              <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                              <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(providerInvoices).paid)}</td>
                              <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(providerInvoices).debt)}</td>
                              {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(providerInvoices).tax)}</td>}
                              <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(providerInvoices).total)}</td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </>
                      }
                    </>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={11 + (vatSubject ? 1 : 0)} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              {!button.group && <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>}
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ProviderInvoiceListPage;
